import React, { memo, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import { Product, User, ProductType } from '../types';
import ProductFilters from '../components/ProductFilters';
import { formatPrice } from '../utils/formatters';
import { productTypeKey, productTypeLabel } from '../utils/productType';
import { formatDate } from '../utils/dates';
import './ProductCard.css';

interface ProductTypeFilters {
  [productTypeId: string]: {
    name: string;
    brand: string;
    country: string;
    region: string;
    category_id: string;
    subcategory_id: string;
    vintage: string;
    grape_variety: string;
    characteristics: string;
    alcoholMin: string;
    alcoholMax: string;
    priceMin: string;  // Add this
    priceMax: string;  // Add this
    features: string;
  };
}

const Dashboard: React.FC = memo(() => {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentProductType, setCurrentProductType] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  
  // Separate filter state for each product type
  const [productTypeFilters, setProductTypeFilters] = useState<ProductTypeFilters>({});
  
  // Global filters that apply to all product types
  const [globalFilters, setGlobalFilters] = useState({
    userId: 'all'
  });
  
  // Add local state for price inputs (separate from filters) - MOVE THIS UP
  const [priceInputs, setPriceInputs] = useState({
    priceMin: '',
    priceMax: ''
  });
  
  // Add ref for debounce timers - MOVE THIS UP
  const priceDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch initial data
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/product-types');
      setProductTypes(response.data);
    } catch (err: any) {
      console.error('Error fetching product types:', err);
    }
  }, []);

  // Handle product type change
  const handleProductTypeChange = useCallback((typeId: string) => {
    console.log('Switching product type to:', typeId);
    
    setCurrentProductType(typeId);
    
    // Clear all filters including price
    const typeKey = typeId || 'all';
    setProductTypeFilters(prev => ({
      ...prev,
      [typeKey]: {
        name: '',
        brand: '',
        country: '',
        region: '',
        category_id: '',
        subcategory_id: '',
        vintage: '',
        grape_variety: '',
        characteristics: '',
        alcoholMin: '',
        alcoholMax: '',
        priceMin: '',
        priceMax: '',
        features: ''
      }
    }));
    
    // Reset price inputs
    setPriceInputs({ priceMin: '', priceMax: '' });
  }, []);

  // Handle filter changes - only affects current product type
  const handleFilterChange = (field: string, value: string) => {
    const typeKey = currentProductType || 'all';

    setProductTypeFilters(prev => {
      const newFiltersForType = {
        ...(prev[typeKey] || {}),
        [field]: value,
      };

      // **CRITICAL FIX**: When a parent filter changes, reset its children.
      if (field === 'product_type_id') {
        newFiltersForType.category_id = '';
        newFiltersForType.subcategory_id = '';
      }
      if (field === 'category_id') {
        newFiltersForType.subcategory_id = '';
      }

      return {
        ...prev,
        [typeKey]: newFiltersForType,
      };
    });
  };

  // Add debounced price filter handler
  const handlePriceInputChange = (field: 'priceMin' | 'priceMax', value: string) => {
    // Update local input state immediately for responsive UI
    setPriceInputs(prev => ({ ...prev, [field]: value }));
    
    // Clear existing timer
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    
    // Set new timer to update filters after 2 seconds
    priceDebounceTimer.current = setTimeout(() => {
      handleFilterChange(field, value);
    }, 2000);
  };
  
  // Handle blur event - apply filter immediately
  const handlePriceBlur = (field: 'priceMin' | 'priceMax') => {
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    handleFilterChange(field, priceInputs[field]);
  };
  
  // Handle Enter key - apply filter immediately
  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'priceMin' | 'priceMax') => {
    if (e.key === 'Enter') {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
      handleFilterChange(field, priceInputs[field]);
    }
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
    };
  }, []);
  
  // Update local price inputs when filters change
  useEffect(() => {
    const typeKey = currentProductType || 'all';
    const currentTypeFilters = productTypeFilters[typeKey];
    if (currentTypeFilters) {
      setPriceInputs({
        priceMin: currentTypeFilters.priceMin || '',
        priceMax: currentTypeFilters.priceMax || ''
      });
    } else {
      // If no filters exist for this type, reset price inputs
      setPriceInputs({
        priceMin: '',
        priceMax: ''
      });
    }
  }, [currentProductType, productTypeFilters]);

  // Get current filters with price inputs - FIX THIS
  const getCurrentFilters = useCallback(() => {
    const typeKey = currentProductType || 'all';
    const typeSpecificFilters = productTypeFilters[typeKey] || {
      name: '',
      brand: '',
      country: '',
      region: '',
      category_id: '',
      subcategory_id: '',
      vintage: '',
      grape_variety: '',
      characteristics: '',
      alcoholMin: '',
      alcoholMax: '',
      priceMin: '',
      priceMax: '',
      features: ''
    };
    
    const currentFilters = {
      ...globalFilters,
      product_type_id: currentProductType,
      ...typeSpecificFilters,
      // Override with local price inputs for display in UI
      priceMin: priceInputs.priceMin,
      priceMax: priceInputs.priceMax
    };
    
    console.log('Current filters for type', currentProductType, ':', currentFilters);
    return currentFilters;
  }, [currentProductType, globalFilters, productTypeFilters, priceInputs]);

  // Reset filters for current product type only
  const resetCurrentTypeFilters = useCallback(() => {
    const typeKey = currentProductType || 'all';
    setProductTypeFilters(prev => ({
      ...prev,
      [typeKey]: {
        name: '',
        brand: '',
        country: '',
        region: '',
        category_id: '',
        subcategory_id: '',
        vintage: '',
        grape_variety: '',
        characteristics: '',
        alcoholMin: '',
        alcoholMax: '',
        priceMin: '',  // Add this
        priceMax: '',  // Add this
        features: ''
      }
    }));
    setPriceInputs({ priceMin: '', priceMax: '' });
  }, [currentProductType]);

  // Delete product function
  const deleteProduct = useCallback(async (productId: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
      return;
    }

    try {
      await axios.delete(`/api/products/${productId}`);
      setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
      setSuccessMessage('Produkt został usunięty');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err));
      console.error('Error deleting product:', err);
      setTimeout(() => setError(''), 5000);
    }
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get filters directly instead of calling getCurrentFilters
        const typeKey = currentProductType || 'all';
        const currentFilters = {
          ...globalFilters,
          product_type_id: currentProductType,
          ...(productTypeFilters[typeKey] || {
            name: '',
            brand: '',
            country: '',
            region: '',
            category_id: '',
            subcategory_id: '',
            vintage: '',
            grape_variety: '',
            characteristics: '',
            alcoholMin: '',
            alcoholMax: '',
            priceMin: '',
            priceMax: '',
            features: ''
          })
        };
        
        // Build query parameters - ONLY include non-empty values
        const params = new URLSearchParams();
        
        Object.entries(currentFilters).forEach(([key, value]) => {
          if (value && value !== '' && value !== 'all') {
            // Convert camelCase to snake_case for API
            let apiKey = key;
            if (key === 'priceMin') apiKey = 'price_min';
            if (key === 'priceMax') apiKey = 'price_max';
            if (key === 'alcoholMin') apiKey = 'alcohol_content_min';
            if (key === 'alcoholMax') apiKey = 'alcohol_content_max';
            
            params.append(apiKey, value.toString());
          }
        });

        const queryString = params.toString();
        console.log('API call with params:', queryString); // Debug log
        
        const response = await axios.get(`/api/products${queryString ? `?${queryString}` : ''}`);
        setProducts(response.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError(extractErrorMessage(error));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentProductType, globalFilters, productTypeFilters]); // Changed dependencies

  // Initial data fetch
  useEffect(() => {
    fetchUsers();
    fetchProductTypes();
  }, [fetchUsers, fetchProductTypes]);

  // Get current filters for passing to child components
  const currentFilters = useMemo(() => getCurrentFilters(), [getCurrentFilters]);

  // Get unique options from products for filters - Use useMemo
  const brandOptions = useMemo(() => 
    Array.from(new Set(products.map(p => p.brand).filter((b): b is string => !!b))).sort(),
    [products]
  );
  
  const countryOptions = useMemo(() => 
    Array.from(new Set(products.map(p => p.country).filter((c): c is string => !!c))).sort(),
    [products]
  );
  
  const yearOptions = useMemo(() => 
    Array.from(new Set(products.map(p => p.vintage).filter((y): y is number => !!y))).sort((a, b) => b - a),
    [products]
  );
  
  const userOptions = useMemo(() => 
    users.map(user => ({ id: user.id, username: user.username })),
    [users]
  );

  if (loading) {
    return <div className="loading">Ładowanie produktów...</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div className="page-header">
          <div className="page-header__title">
            <h1>Baza Produktów Alkoholowych</h1>
            <span className="product-count">{products.length}</span>
          </div>
          <div className="page-header__actions">
            <Link to="/products-table" className="btn btn-secondary">
              Widok tabeli
            </Link>
            <Link to="/product-editor" className="btn btn-primary">
              Dodaj Produkt
            </Link>
          </div>
        </div>

        {/* Filtry */}
        <div className="page-section">
          <ProductFilters
            filters={getCurrentFilters()}
            onChange={handleFilterChange}
            onProductTypeChange={handleProductTypeChange}
            onUserFilterChange={(value) => handleFilterChange('userId', value)}
            onPriceChange={handlePriceInputChange}
            onPriceBlur={handlePriceBlur}
            onPriceKeyDown={handlePriceKeyDown}
            resetFilters={resetCurrentTypeFilters}
            brandOptions={brandOptions}
            categoryOptions={[]}
            countryOptions={countryOptions}
            regionOptions={[]}
            vintageOptions={yearOptions}
            userOptions={userOptions}
            productTypes={productTypes}
            resultCount={products.length}
            className="dashboard-filters"
          />
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {products.length === 0 ? (
          <div className="empty-state">
            <h3>Brak produktów</h3>
            <p>Dodaj pierwszy produkt do bazy danych!</p>
            <Link to="/product-editor" className="btn btn-primary">
              Dodaj Produkt
            </Link>
          </div>
        ) : (
          <div className="product-grid">
            {products.map((product) => {
              // Brand and origin describe the bottle rather than measure it, so
              // they read better as a subtitle than as two more spec cells.
              const origin = [product.country, product.region].filter(Boolean).join(', ');
              const subtitle = [product.brand, origin].filter(Boolean).join(' · ');

              const specs: { label: string; value: React.ReactNode }[] = [];
              if (product.vintage) specs.push({ label: 'Rocznik', value: product.vintage });
              if (product.alcohol_content) {
                specs.push({ label: 'Alkohol', value: `${product.alcohol_content}%` });
              }
              if (product.volume) specs.push({ label: 'Pojemność', value: `${product.volume} ml` });
              specs.push({
                label: 'Dodano',
                value: formatDate(product.created_at),
              });

              return (
                <div key={product.id} className="product-card">
                  {product.image_url && (
                    <div className="product-card__media">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        onError={(e) => {
                          // Hide the whole plate, not just the img -- otherwise a
                          // broken URL leaves an empty cream rectangle. Cards with
                          // no image_url render with no plate at all, so this
                          // matches them.
                          const el = e.target as HTMLImageElement;
                          el.parentElement?.style.setProperty('display', 'none');
                        }}
                      />
                    </div>
                  )}

                  <div className="product-card__body">
                    <div className="product-card__head">
                      {/* The chip sits above the name rather than beside it.
                          Sharing a row, a label like "Wysokoprocentowe" won and
                          broke names across three lines. */}
                      <span className={`type-badge ${productTypeKey(product.product_type_id, productTypes)}`}>
                        {productTypeLabel(product.product_type_id, productTypes)}
                      </span>
                      <div>
                        <h3 className="product-card__name">{product.name}</h3>
                        {subtitle && <div className="product-card__sub">{subtitle}</div>}
                      </div>
                    </div>

                    <dl className="product-card__specs">
                      {specs.map((spec) => (
                        <div key={spec.label}>
                          <dt>{spec.label}</dt>
                          <dd>{spec.value}</dd>
                        </div>
                      ))}
                    </dl>

                    {product.description_short && (
                      <p className="product-card__desc">{product.description_short}</p>
                    )}

                    <div className="product-card__foot">
                      <div
                        className={
                          product.price_net == null
                            ? 'product-card__price product-card__price--none'
                            : 'product-card__price'
                        }
                      >
                        {product.price_net == null ? 'Brak ceny' : formatPrice(product.price_net)}
                        {product.price_alt != null && (
                          <span className="product-card__price-alt">
                            alt. {formatPrice(product.price_alt)}
                          </span>
                        )}
                      </div>

                      <div className="product-card__actions">
                        <button
                          onClick={() => navigate(`/product-editor/${product.id}`)}
                          className="product-card__btn"
                          type="button"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => deleteProduct(product.id)}
                          className="product-card__btn product-card__btn--danger"
                          type="button"
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default Dashboard;
